import React, { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Check, X, Trash2, CreditCard } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminResellerSubscriptions() {
  const { getAdminAxios } = useOutletContext();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getAdminAxios().get(`${API}/admin/reseller-subscriptions`);
      setItems(data);
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  }, [getAdminAxios]);

  useEffect(() => { refresh(); }, [refresh]);

  const approve = async (id) => {
    setActing(id);
    try { await getAdminAxios().post(`${API}/admin/reseller-subscriptions/${id}/approve`); await refresh(); }
    catch (e) { alert(e.response?.data?.detail || e.message); }
    finally { setActing(null); }
  };
  const reject = async (id) => {
    if (!window.confirm("¿Rechazar esta solicitud?")) return;
    setActing(id);
    try { await getAdminAxios().post(`${API}/admin/reseller-subscriptions/${id}/reject`); await refresh(); }
    catch (e) { alert(e.response?.data?.detail || e.message); }
    finally { setActing(null); }
  };
  const remove = async (id) => {
    if (!window.confirm("¿Eliminar?")) return;
    try { await getAdminAxios().delete(`${API}/admin/reseller-subscriptions/${id}`); await refresh(); }
    catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  const STATUS = {
    pending: { txt: "Pendiente", cls: "bg-amber-500/20 text-amber-300 border-amber-500/30" },
    approved: { txt: "Aprobado", cls: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" },
    rejected: { txt: "Rechazado", cls: "bg-red-500/20 text-red-300 border-red-500/30" },
  };

  return (
    <div className="p-8 max-w-6xl" data-testid="admin-reseller-subscriptions-page">
      <h1 className="text-2xl font-semibold text-white flex items-center gap-2 mb-1"><CreditCard className="w-6 h-6" /> Suscripciones de Reseller</h1>
      <p className="text-sm text-slate-400 mb-6">Pagos por transferencia que enviaron los sub super admins. Aprobá para activarles el plan.</p>

      {error && <div className="mb-4 rounded-md border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-300">⚠️ {error}</div>}

      <Card className="bg-[#13112b] border-indigo-900/30">
        <CardHeader>
          <CardTitle className="text-white text-base">Solicitudes ({items.length})</CardTitle>
          <CardDescription className="text-slate-400">Aprobar extiende automáticamente el plan del sub admin desde su vencimiento actual o desde hoy.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center gap-2 text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>
          ) : items.length === 0 ? (
            <p className="text-sm text-slate-500">Sin solicitudes.</p>
          ) : (
            <div className="space-y-2">
              {items.map((s) => {
                const st = STATUS[s.status] || STATUS.pending;
                return (
                  <div key={s.id} className="border border-white/10 rounded-lg p-4 bg-black/20" data-testid={`reseller-sub-${s.id}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <p className="text-white font-medium">{s.email} <span className="text-xs text-slate-500">({s.name})</span></p>
                        <p className="text-xs text-slate-400 mt-0.5">
                          {s.plan_name} · ${s.plan_price} {s.plan_currency} · {s.plan_days} días · {s.max_clients} clientes
                        </p>
                        {s.reference && <p className="text-xs text-slate-500 mt-1">Ref: <code className="bg-black/30 px-1 rounded">{s.reference}</code></p>}
                        <p className="text-[10px] text-slate-500 mt-1">{(s.created_at || "").slice(0, 16).replace("T", " ")}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`text-xs px-2 py-0.5 rounded-full border ${st.cls}`}>{st.txt}</span>
                        {s.status === "pending" && (
                          <>
                            <Button size="sm" onClick={() => approve(s.id)} disabled={acting === s.id} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid={`approve-reseller-sub-${s.id}`}>
                              {acting === s.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => reject(s.id)} className="border-red-500/30 text-red-300 hover:bg-red-500/10" data-testid={`reject-reseller-sub-${s.id}`}><X className="w-3 h-3" /></Button>
                          </>
                        )}
                        <Button size="sm" variant="outline" onClick={() => remove(s.id)} className="border-white/10 text-slate-400 hover:bg-white/5"><Trash2 className="w-3 h-3" /></Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
